import { FC } from 'react';
import { Box, Card, formatAmount, formatDate, Tooltip, toPascalCase, withStyles } from 'vopay';
import styles from './VerificationCard.module.scss';
import { Button } from '@/components/UI';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { InteracWithExtra } from '../PaymentMethodDetails';
import useClientAccountPaymentDetails from '@/hooks/usePaymentMethodDetails';
import { useToast } from '@/providers/Toast.provider';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/providers/Dialog.provider';
import UpgradePlanDialog from '@/components/Customer/Tabs/PaymentMethods/UpgradePlanDialog/UpgradePlanDialog';
import EnhancedVerificationDialog from '@/components/Customer/Tabs/PaymentMethods/EnhancedVerificationDialog/EnhancedVerificationDialog';

const cn = withStyles(styles);

type VerificationCardProps = {
  interac: InteracWithExtra;
  accountID: string;
  email: string;
  isInEditMode?: boolean;
  onVerify: () => void;
};

const VerificationCard: FC<VerificationCardProps> = ({ interac, accountID, email, isInEditMode, onVerify }) => {
  const { InteracBasicVerificationEnabled, InteracEnhancedVerificationEnabled } = useLockedFeatures();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.Details' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const dialog = useDialog();

  const { verifyInteracAccount } = useClientAccountPaymentDetails({
    accountID,
    email,
  });

  const handleInteracSendVerification = async () => {
    const closeType = await alertDialog.open(
      t('Verification.Dialog.sendVerificationTitle', { defaultValue: 'Send Verification Request?' }),
      t('Verification.Dialog.sendVerificationMessage', {
        defaultValue:
          'We will check if the name provided for this email address matches the registered name on Interac.',
      }),
      {
        confirmLabel: t('Verification.Dialog.confirm', { defaultValue: 'Confirm' }),
        bottom: t('Verification.Dialog.sendVerificationBottom', {
          defaultValue: 'This action does not notify the client',
        }),
        abortLabel: t('Verification.Dialog.cancel', { defaultValue: 'Cancel' }),
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyInteracAccount({
          VerificationTier: 'basic',
          EmailAddress: interac.RecipientEmailAddress || '',
          ContactID: accountID,
        });
        if (res.Success) {
          toast.success(
            t('Verification.Toast.verificationRequestSent', { defaultValue: 'Interac Verification request sent' }),
          );
          await onVerify();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error(t('error', { defaultValue: 'Error' }), err.message);
      }
    }
  };

  const handleEnhancedInteracSendVerification = async () => {
    const closeType = await alertDialog.open(
      t('Verification.Dialog.sendVerificationTitle', { defaultValue: 'Send Verification Request?' }),

      <Box flx="col" ai="fs" p="sm">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            gap: '24px',
            color: 'rgba(45, 45, 45, 0.75)',
          }}
        >
          <p>
            {t('Verification.Dialog.sendVerificationMessage', {
              defaultValue:
                'We will check if the name provided for this email address matches the registered name on Interac.',
            })}
          </p>
          <div>
            <p>
              {t('Verification.Dialog.enhancedVerificationMessage', {
                defaultValue: 'We will also retrieve the following details:',
              })}
            </p>
            <ul style={{ listStylePosition: 'outside', paddingLeft: '20px' }}>
              <li>
                {t('Verification.Dialog.enhancedVerificationDetails.registeredName', {
                  defaultValue: 'Registered Interac Name',
                })}
              </li>
              <li>{t('Verification.Dialog.enhancedVerificationDetails.alias', { defaultValue: 'Alias' })}</li>
              <li>
                {t('Verification.Dialog.enhancedVerificationDetails.accountType', { defaultValue: 'Account Type' })}
              </li>
              <li>
                {t('Verification.Dialog.enhancedVerificationDetails.autoDepositStatus', {
                  defaultValue: 'Auto-Deposit Status',
                })}
              </li>
              <li>
                {t('Verification.Dialog.enhancedVerificationDetails.transactionLimit', {
                  defaultValue: 'Transaction Limit',
                })}
              </li>
            </ul>
          </div>
        </div>
      </Box>,
      {
        confirmLabel: t('Verification.Dialog.confirm', { defaultValue: 'Confirm' }),
        bottom: t('Verification.Dialog.sendVerificationBottom', {
          defaultValue: 'This action does not notify the client',
        }),
        abortLabel: t('Verification.Dialog.cancel', { defaultValue: 'Cancel' }),
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyInteracAccount({
          VerificationTier: 'enhanced',
          EmailAddress: interac.RecipientEmailAddress || '',
          ContactID: accountID,
        });

        if (res.Success) {
          toast.success(
            t('Verification.Toast.verificationRequestSent', { defaultValue: 'Interac Verification request sent' }),
          );
          await onVerify();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error(t('error', { defaultValue: 'Error' }), err.message);
      }
    }
  };

  function getAutoDepositStatus(value: string): string {
    if (value === '1') return t('Verification.enabled', { defaultValue: 'Enabled' });
    if (value === '0') return t('Verification.disabled', { defaultValue: 'Disabled' });
    if (value === 'N/A') return 'N/A';
    return '-';
  }

  function getTransactionLimit(value: string): string {
    if (value === 'N/A') return 'N/A';
    if (value) return `${interac.Currency} ${formatAmount(interac.TransactionLimit)}`;
    return '-';
  }

  const rows = [
    {
      key: t('Verification.status', { defaultValue: 'Verification Status' }),
      val: (
        <>
          <p className={cn('verificationLabel', interac.verificationStatus)}>
            {interac.verificationStatus === 'verified' ? (
              t('Verification.verified', { defaultValue: 'Verified' })
            ) : interac.verificationStatus === 'unverified' ? (
              <>
                {t('Verification.notVerified', { defaultValue: 'Not Verified' })}
                {interac.verificationSentOn && (
                  <Tooltip
                    position="topLeft"
                    icon="info"
                    information={t('Verification.notRegisteredTooltip', {
                      defaultValue:
                        'This address could not be verified because it is not registered with Interac. Please ensure the email is linked to an Interac account.',
                    })}
                  />
                )}
              </>
            ) : (
              t('Verification.failed', { defaultValue: 'Verification Failed' })
            )}
          </p>
        </>
      ),
    },
    {
      key: t('Verification.registeredInteracName', { defaultValue: 'Registered Interac Name' }),
      val: interac.RegisteredName,
    },
    { key: t('Verification.alias', { defaultValue: 'Alias' }), val: interac.Alias },
    { key: t('Verification.accountType', { defaultValue: 'Account Type' }), val: toPascalCase(interac.CustomerType) },
    {
      key: t('Verification.autoDeposit', { defaultValue: 'Auto-Deposit' }),
      val: getAutoDepositStatus(interac.AutoDepositEnabled),
    },
    {
      key: t('Verification.transactionLimit', { defaultValue: 'Transaction Limit' }),
      val: getTransactionLimit(interac.TransactionLimit),
    },
  ].map((row, index) => ({ key: row.key, val: row.val || index === 0 ? row.val : '-' }));

  const interacVerificationLockedStyle = !InteracBasicVerificationEnabled
    ? 'fullyLocked'
    : !interac.verificationSentOn && !InteracEnhancedVerificationEnabled
      ? 'partiallyLocked'
      : undefined;

  async function handleUnlockVerificationRequestClick() {
    await dialog.open<boolean>((args) => <UpgradePlanDialog {...args} />);
  }

  async function handleUnlockEnhancedVerificationRequestClick() {
    await dialog.open<boolean>((args) => <EnhancedVerificationDialog {...args} />);
  }

  return (
    <div
      className={cn(
        'verificationCard',
        InteracEnhancedVerificationEnabled && 'enhanced',
        interacVerificationLockedStyle,
      )}
    >
      <Card style={{ padding: '16px' }} hoverEffect={false}>
        <span className={cn('cardContent')}>
          <div className={cn('header')}>
            <h5 className={cn('title')}>{t('Verification.title', { defaultValue: 'Verification Details' })}</h5>
            {interac?.verificationSentOn && (
              <p className={cn('requestSentOn')}>
                {t('Verification.lastRequestSentOn', { defaultValue: 'Last request sent on' })}{' '}
                {formatDate(interac.verificationSentOn, 'yyyy-MM-dd HH:mm:ss')}
              </p>
            )}
          </div>
          <span>
            {rows.map(({ key, val }) => (
              <div key={key} className={cn('row')}>
                <div>{key}</div>
                <div>{val}</div>
              </div>
            ))}
          </span>
          <div className={cn('footer')}>
            {!InteracBasicVerificationEnabled && (
              <div className={cn('buttonContainer')}>
                <Button
                  iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                  size="small"
                  colorScheme="primary"
                  disabled={isInEditMode}
                  onClick={handleUnlockVerificationRequestClick}
                >
                  {t('Verification.unlockVerificationRequests', { defaultValue: 'Unlock Verification Request ss' })}
                </Button>
              </div>
            )}

            {InteracBasicVerificationEnabled && (
              <div className={cn('buttonContainer')}>
                <Button size="small" onClick={() => handleInteracSendVerification()}>
                  {t('Verification.basicVerificationRequest', { defaultValue: 'Basic Verification Request' })}
                </Button>

                {!InteracEnhancedVerificationEnabled ? (
                  <Button
                    colorScheme="primary"
                    iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                    size="small"
                    onClick={handleUnlockEnhancedVerificationRequestClick}
                  >
                    {t('Verification.unlockEnhancedVerification', { defaultValue: 'Unlock Enhanced Verification ddd' })}
                  </Button>
                ) : (
                  <Button
                    colorScheme="feature"
                    size="small"
                    iconRightProps={{ icon: 'auto_awesome', color: '#fbfbfb', fillAmount: 1 }}
                    onClick={handleEnhancedInteracSendVerification}
                  >
                    {t('Verification.enhancedVerificationRequest', { defaultValue: 'Enhanced Verification Request' })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </span>
      </Card>
    </div>
  );
};

export default VerificationCard;
